/**
 * Form-field schemas per connector, mirroring what the backend integration
 * classes actually read (see the field notes on `IntegrationConfiguration`).
 * Drives the add/edit form: which fields to render, which are required, and
 * which sit behind the "Advanced" divider.
 */

export interface ConnectorFormField {
  key: string;
  label: string;
  type: 'text' | 'url' | 'password' | 'number' | 'select';
  required?: boolean;
  /** Options for `select` fields. */
  options?: string[];
  helper?: string;
  /** Render under the "Advanced" divider (optional tuning fields). */
  advanced?: boolean;
}

export interface ConnectorFormSchema {
  fields: ConnectorFormField[];
  /**
   * Sepioo-style credentials: either a single `password` or the split pair
   * `pickingPassword` (button/blink ops) + `updatePassword` (image/label ops).
   * When set, the form renders a credential-mode toggle; the password fields
   * are NOT part of `fields`.
   */
  splitCredentials?: boolean;
}

const REQUEST_TIMEOUT: ConnectorFormField = {
  key: 'requestTimeout',
  label: 'Request timeout (s)',
  type: 'number',
  advanced: true,
};

const OMLOX_FIELDS: ConnectorFormField[] = [
  { key: 'apiUrl', label: 'API URL', type: 'url', required: true },
  { key: 'wsUrl', label: 'WebSocket URL', type: 'url', helper: 'Used for live event streaming (fence events).' },
  {
    key: 'providerIdStrategy',
    label: 'Provider-ID strategy',
    type: 'select',
    options: ['none', 'convert_to_base_10'],
    helper: 'How device ids are converted to omlox provider ids.',
  },
  REQUEST_TIMEOUT,
];

export const CONNECTOR_FORM_SCHEMAS: Record<string, ConnectorFormSchema> = {
  sepioo: {
    splitCredentials: true,
    fields: [
      { key: 'baseUrl', label: 'Base URL', type: 'url', required: true },
      { key: 'account', label: 'Account', type: 'text', required: true },
      { key: 'location', label: 'Location', type: 'text', required: true },
      { key: 'username', label: 'Username', type: 'text', required: true },
      REQUEST_TIMEOUT,
    ],
  },
  'solum-aims': {
    fields: [
      { key: 'baseUrl', label: 'Base URL', type: 'url', required: true, helper: 'AIMS portal endpoint.' },
      REQUEST_TIMEOUT,
      { key: 'paginationSize', label: 'Pagination size', type: 'number', advanced: true },
      { key: 'paginationConcurrency', label: 'Pagination concurrency', type: 'number', advanced: true },
      { key: 'blinkChunkSize', label: 'Blink chunk size', type: 'number', advanced: true },
      // { key: 'buttonConfigId', label: 'Button config id', type: 'number', advanced: true },
    ],
  },
  'solum-aims-saas': {
    fields: [
      { key: 'baseUrl', label: 'Base URL', type: 'url', required: true },
      { key: 'username', label: 'Username', type: 'text', required: true },
      { key: 'password', label: 'Password', type: 'password', required: true },
      { key: 'company', label: 'Company', type: 'text', required: true },
      { key: 'store', label: 'Store', type: 'text', required: true },
      // { key: 'buttonConfigId', label: 'Button config id', type: 'number', advanced: true },
      REQUEST_TIMEOUT,
    ],
  },
  'steute-sensorbridge': {
    fields: [{ key: 'baseUrl', label: 'Base URL', type: 'url', required: true, helper: 'SensorBridge endpoint.' }],
  },
  deephub: { fields: OMLOX_FIELDS },
  'omlox-hub': { fields: OMLOX_FIELDS },
  simulated: { fields: [{ key: 'baseUrl', label: 'Base URL', type: 'url', required: true }] },
  virtual: { fields: [] },
};

export const getConnectorFormSchema = (integrationName: string): ConnectorFormSchema => {
  switch (integrationName) {
    case 'Solum-Label':
      return CONNECTOR_FORM_SCHEMAS['solum-aims'];
    case 'Solum-Cloud-Label':
      return CONNECTOR_FORM_SCHEMAS['solum-aims-saas'];
    case 'PdiDigital-Label':
      return CONNECTOR_FORM_SCHEMAS['sepioo'];
    case 'Steute-Switch':
      return CONNECTOR_FORM_SCHEMAS['steute-sensorbridge'];
    case 'Simulated-Label':
      return CONNECTOR_FORM_SCHEMAS['simulated'];
    case 'Virtual-Device':
      return CONNECTOR_FORM_SCHEMAS['virtual'];
    default:
      if (integrationName.startsWith('Omlox')) {
        return { fields: OMLOX_FIELDS };
      }
      return { fields: [] };
  }
};
